import { useContext, useEffect, useState } from "react";
import CustomTable from "../../components/CustomTable";
import useApi from "../../hooks/useApi";
import { ListCustomer, updateCustomer, ViewStudents } from "../../constants/API/API";
import { AppStateContext } from "../../context/AppContext";
import moment from "moment";
import { EditOutlined, EyeOutlined } from "@ant-design/icons";
import { Button, Card, Col, Descriptions, Form, Input, InputNumber, Select, Modal, Row, DatePicker, Space, Tag, } from "antd";
import { getBelts } from "../../constants/API/API";
import { useNavigate, useParams } from "react-router";
import callApi from "../../constants/API/CallApi";
import { Severty, ShowToast } from "../../helper/toast";
import dayjs from "dayjs";


const ViewStudent = () => {
  const navigate = useNavigate();
  const { setPageHeading } = useContext(AppStateContext);
  setPageHeading("Student List");

  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [details, setDetails] = useState({});
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [refresh, setRefresh] = useState(new Date().getTime());
  const [editModal, setEditModal] = useState(false);

  useApi({
    callback: ViewStudents,
    payload: id,
    dependencies: [refresh],
    shouldCallApi: true,
    onSuccess: ({ data, totalDocs }) => {
      setData(data);
      data?.length && setDetails(data[0]?.parentId || {});
      setTotalDocs(totalDocs);
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
  });

  const handleEditClick = (record) => {
    setSelectedCustomer(record);
    setEditModal(true);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (_, item) => <p>{item?.name}</p>,
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
      render: (_, item) => <p style={{ textTransform: "capitalize" }}>{item?.gender || "-"}</p>,
    },
    {
      title: "Parent",
      dataIndex: "parent",
      key: "parent",
      render: (_, item) => <p>{item?.parentId?.name}</p>,
    },
    {
      title: "Class Limit",
      dataIndex: "limit",
      key: "limit",
      render: (_, item) => <p>{item?.limit}</p>,
    },
    {
      title: "Added At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (_, record) => moment(record.createdAt).format("ll"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            onClick={() => navigate(`/students/view/${record._id}`)}
            icon={<EyeOutlined />}
            title="View"
          />
          <Button
            type="primary"
            onClick={() => handleEditClick(record)}
            icon={<EditOutlined />}
            title="Edit"
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Button
        type="default"
        className="mb-2"
        style={{ color: "black" }}
        onClick={() => window.history.back()}
      >
        Back
      </Button>
      <Card title="Parent Overview" className="mb-4">
        <Descriptions bordered column={2}>
          <Descriptions.Item label="Name">
            {details?.name || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            {details?.email || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Mobile">
            {details?.mobileNumber
              ? "+" + details?.countryCode + " " + details?.mobileNumber
              : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Role">
            {details?.roleType || "Parent"}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={!details?.isActive ? "red" : "green"}>
              {!!details?.isActive ? "Active" : "Inactive"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Joined">
            {moment(details?.created_at).format("ll")}
          </Descriptions.Item>
        </Descriptions>
      </Card>
      <CustomTable
        addFn={false}
        heading={<h5>Students</h5>}
        data={data}
        loading={loading}
        columns={columns}
        totalDocs={totalDocs}
        fields={false}
      />

      {editModal && selectedCustomer &&
        <StudentUpdate
          editVisible={editModal}
          setEditVisible={setEditModal}
          selectedCustomer={selectedCustomer}
          refresh={setRefresh}
        />
      }
    </div>
  );
};

export default ViewStudent;

const StudentUpdate = ({ editVisible, setEditVisible, selectedCustomer, refresh, }) => {
  const [loading, setLoading] = useState(false);
  const [belts, setBelts] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [form] = Form.useForm();

  useApi({
    callback: getBelts,
    payload: selectedBranch ? { branchId: selectedBranch } : null,
    dependencies: [selectedBranch, refresh],
    shouldCallApi: true,
    shouldCallApi: !!selectedBranch,
    onSuccess: ({ data }) => {
      setBelts(data);
    },
  });

  const handleOk = async (values) => {
    setLoading(true);
    const payload = { ...values };
    const currentBeltId = selectedCustomer?.beltId?._id;
    if (payload.beltId === currentBeltId) {
      delete payload.beltId;
    }

    callApi({
      callback: updateCustomer,
      payload: [payload, selectedCustomer._id],
      onSuccess: ({ message }) => {
        ShowToast(message, Severty.SUCCESS);
        refresh(new Date().getTime());
        setEditVisible(false);
        form.resetFields();
        setLoading(false);
      },
      onError: (err) => {
        ShowToast(err, Severty.ERROR);
        setLoading(false);
      },
    });
  };

  const handleCancel = () => {
    setEditVisible(false);
    form.resetFields();
  };

  useEffect(() => {
    if (selectedCustomer) {
      form.setFieldsValue({
        ...selectedCustomer,
        gender: selectedCustomer?.gender || "",
        dob: selectedCustomer?.dob ? dayjs(selectedCustomer.dob) : null,
        beltId: selectedCustomer?.beltId?._id,
      })
      if (selectedCustomer?.parentId?.branchId) {
        setSelectedBranch(selectedCustomer.parentId.branchId);
      }
    }
  }, [selectedCustomer]);

  return (
    <Modal
      width={800}
      centered
      open={editVisible}
      className="tab_modal"
      title="Update Customer"
      okButtonProps={{
        htmlType: "submit",
        form: "update-customer",
        loading: loading,
      }}
      onCancel={handleCancel}
    >
      <Form
        id="update-customer"
        form={form}
        layout="vertical"
        onFinish={handleOk}
      >
        <Row gutter={[12, 0]}>
          {/* Name */}
          <Col span={24} md={12}>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: "Student name is required!" }]}
            >
              <Input placeholder="Enter name" />
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item
              name="gender"
              label="Gender"
            >
              <Select placeholder="Select Gender" allowClear>
                <Select.Option value="male">Male</Select.Option>
                <Select.Option value="female">Female</Select.Option>
                <Select.Option value="other">Other</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item
              name="dob"
              label="Date of Birth"
              rules={[{ required: true, message: "Please select DOB" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                disabledDate={(current) =>
                  current && current > dayjs().endOf("day")
                }
                onChange={(date) => {
                  if (date) {
                    const age = dayjs().diff(date, "year");
                    form.setFieldsValue({ age });
                  } else {
                    form.setFieldsValue({ age: undefined });
                  }
                }}
              />
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item name="age" label="Age">
              <InputNumber disabled style={{ width: "100%" }} />
            </Form.Item>
          </Col>


          <Col span={24} md={12}>
            <Form.Item
              name="limit"
              label="Class Limit"
              rules={[{ required: true, message: "Please enter class limit!" }]}
            >
              <InputNumber
                type="number"
                min={0}
                max={100}
                placeholder="Enter Class Limit"
              />
            </Form.Item>
          </Col>

          <Col span={24} sm={12} md={12}>
            <Form.Item
              name="beltId"
              label="Belt"
              rules={[{ required: true, message: "Please select a belt!" }]}
            >
              <Select placeholder="Select Belt">
                {belts?.map((belt) => (
                  <Select.Option key={belt?._id} value={belt?._id}>
                    {belt?.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>


          {/* Profile Upload */}
          {/* <Col span={24}>
            <Form.Item label="Upload Profile" name="profilePic">
              <Upload
                listType="picture"
                maxCount={1}
                beforeUpload={beforeUpload}
                customRequest={handleChange}
                onRemove={handleRemove}
                fileList={file}
                action=""
              >
                {file && file.length > 0 ? null : (
                  <Button icon={<UploadOutlined />}>Upload</Button>
                )}
              </Upload>
              {image && typeof image === "string" && image !== "" && (
                <div className="mt-3">
                  <Image width={100} src={image} />
                </div>
              )}
            </Form.Item>
          </Col> */}
        </Row>
      </Form>
    </Modal>
  );
};
